import React, { useRef, useState } from 'react';
import { Plus, Trash2, Save, Loader2, FlaskConical } from 'lucide-react';
import { Switch } from '../ui/switch';
import { TokenChips, insertAtCaret } from './TokenChips';
import { requestDriveRenameCheck } from './DriveRenamePrompt';

const FIELDS = [
  ['root_folder', 'Top folder in Drive'],
  ['client_folder', 'Client folder'],
  ['property_folder', 'Property folder (inside client)'],
  ['job_folder', 'Job folder (inside property · one per appraisal / child quote)'],
  ['client_docs_folder', 'Client-level uploads folder'],
  ['property_docs_folder', 'Property-level uploads folder'],
];
const BUILT_IN = ['appraisal', 'quotes', 'accepted', 'work', 'completion', 'uploads'];

export const FolderLayoutTab = ({ api, overview, onSaved, toast }) => {
  const [form, setForm] = useState(overview.layout);
  const [saving, setSaving] = useState(false);
  const focused = useRef({ key: null, el: null });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setStage = (i, v) => set('stages', form.stages.map((s, j) => (j === i ? { ...s, name: v } : s)));

  const insert = (token) => {
    const { key, el } = focused.current;
    if (!key) return;
    if (key.startsWith('stage:')) { const i = +key.slice(6); setStage(i, insertAtCaret(el, form.stages[i].name, token)); }
    else set(key, insertAtCaret(el, form[key], token));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/drive/layout', form);
      toast.success('Folder layout saved');
      onSaved();
      requestDriveRenameCheck({ all: true });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const bind = (key) => ({ onFocus: (e) => { focused.current = { key, el: e.target }; } });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4" data-testid="drive-folder-layout-tab">
      <div className="bg-white border border-[#E2E2DF] rounded-md p-5 space-y-4">
        {FIELDS.map(([k, label]) => (
          <div key={k}>
            <label className="text-xs font-semibold text-slate-700">{label}</label>
            <input data-testid={`layout-input-${k}`} value={form[k] || ''} {...bind(k)}
              onChange={(e) => set(k, e.target.value)} placeholder={overview.layout_defaults[k]}
              className="mt-1 w-full px-3 py-2 text-xs font-mono border border-[#E2E2DF] rounded focus:outline-none focus:ring-2 focus:ring-[#0F4F1B]/30" />
          </div>
        ))}
        <div>
          <div className="text-xs font-semibold text-slate-700 mb-1">Stage folders (inside each job folder · created only when a file lands)</div>
          <div className="space-y-1.5">
            {form.stages.map((s, i) => (
              <div key={s.key} className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-slate-400 w-24 truncate">{s.key}</span>
                <input data-testid={`layout-stage-${s.key}`} value={s.name} {...bind(`stage:${i}`)}
                  onChange={(e) => setStage(i, e.target.value)}
                  className="flex-1 px-3 py-1.5 text-xs border border-[#E2E2DF] rounded focus:outline-none focus:ring-2 focus:ring-[#0F4F1B]/30" />
                {!BUILT_IN.includes(s.key) && (
                  <button data-testid={`layout-stage-remove-${s.key}`} onClick={() => set('stages', form.stages.filter((_, j) => j !== i))}
                    className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>
                )}
              </div>
            ))}
          </div>
          <button data-testid="layout-add-stage-btn" onClick={() => set('stages', [...form.stages, { key: `custom_${form.stages.length + 1}`, name: `${form.stages.length + 1} New Folder` }])}
            className="mt-2 inline-flex items-center gap-1 text-xs text-[#0F4F1B] font-semibold hover:underline"><Plus size={12} /> Add stage folder</button>
        </div>
        {!overview.connected && (
          <div className="flex items-center justify-between gap-3 p-3 rounded border border-amber-200 bg-amber-50">
            <div className="flex items-start gap-2 text-xs text-amber-800"><FlaskConical size={14} className="mt-0.5" />
              <div><b>Test mode</b> — Drive isn't connected here. Turn on to simulate Drive so you can try the folder rules without touching real Drive.</div></div>
            <Switch data-testid="layout-simulate-switch" checked={!!form.simulate} onCheckedChange={(v) => set('simulate', v)} />
          </div>
        )}
        <div className="flex justify-end">
          <button data-testid="layout-save-btn" onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-md bg-[#0F4F1B] text-white hover:bg-[#0a3c14] disabled:opacity-50 transition-colors">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save layout
          </button>
        </div>
      </div>
      <TokenChips tokens={overview.tokens} onInsert={insert} />
    </div>
  );
};
